import { useEffect, useRef, useState, useCallback } from 'react';
import type { AvatarConfig, PaletteColors } from './avatarTypes';

export interface AnimationOffsets {
  breathY: number;
  blinkFrame: boolean;
}

interface Particle {
  x: number;
  y: number;
  speed: number;
  color: string;
  life: number;
  maxLife: number;
}

/**
 * Manages idle animations (blink, breathe, particles) for the avatar canvas.
 * Returns animation offsets that should be passed to AvatarCanvas, plus a
 * draw function for particles that runs on the canvas directly.
 */
export function useAvatarAnimations(
  canvasRef: React.RefObject<HTMLCanvasElement | null>,
  config: AvatarConfig,
  enabled = true,
) {
  const [offsets, setOffsets] = useState<AnimationOffsets>({
    breathY: 0,
    blinkFrame: false,
  });

  const particlesRef = useRef<Particle[]>([]);
  const rafRef = useRef<number>(0);
  const disabledRef = useRef(!enabled);
  const lastBlinkRef = useRef(0);
  const nextBlinkRef = useRef(randomInterval());
  const blinkDurationRef = useRef(0);

  const spawnParticle = useCallback((palette: PaletteColors, gridSize: number, pixelScale: number) => {
    const colors = [palette.bodyPrimary, palette.eyeColor, palette.accessoryColor];
    const canvasSize = gridSize * pixelScale;
    particlesRef.current.push({
      x: Math.random() * canvasSize,
      y: canvasSize,
      speed: 0.3 + Math.random() * 0.5,
      color: colors[Math.floor(Math.random() * colors.length)],
      life: 0,
      maxLife: 120 + Math.random() * 60,
    });
  }, []);

  useEffect(() => {
    if (!enabled) {
      disabledRef.current = true;
      setOffsets({ breathY: 0, blinkFrame: false });
      return;
    }
    disabledRef.current = false;

    let startTime = performance.now();

    const tick = (now: number) => {
      if (disabledRef.current) return;
      const elapsed = (now - startTime) / 1000;

      // Breathing: gentle sine wave, ~3s period
      const breathY = Math.sin(elapsed * (2 * Math.PI / 3)) * 0.5;

      // Blink logic
      let blinkFrame = false;
      const timeSinceBlink = now - lastBlinkRef.current;
      if (blinkDurationRef.current > 0) {
        blinkDurationRef.current -= 16;
        blinkFrame = true;
        if (blinkDurationRef.current <= 0) {
          blinkDurationRef.current = 0;
          lastBlinkRef.current = now;
          nextBlinkRef.current = randomInterval();
        }
      } else if (timeSinceBlink > nextBlinkRef.current) {
        blinkDurationRef.current = 150; // 150ms blink
        blinkFrame = true;
      }

      setOffsets({ breathY, blinkFrame });

      // Particles
      const canvas = canvasRef.current;
      if (canvas) {
        const ctx = canvas.getContext('2d');
        if (ctx) {
          // Spawn occasionally
          if (Math.random() < 0.02 && particlesRef.current.length < 5) {
            spawnParticle(config.palette, config.gridSize, canvas.width / config.gridSize);
          }

          // Update & draw particles
          const ps = particlesRef.current;
          for (let i = ps.length - 1; i >= 0; i--) {
            const p = ps[i];
            p.y -= p.speed;
            p.x += Math.sin(p.life * 0.05) * 0.3;
            p.life++;

            const alpha = 1 - p.life / p.maxLife;
            if (alpha <= 0) {
              ps.splice(i, 1);
              continue;
            }

            ctx.globalAlpha = alpha * 0.6;
            ctx.fillStyle = p.color;
            const pxSize = canvas.width / config.gridSize;
            ctx.fillRect(p.x, p.y, pxSize * 0.5, pxSize * 0.5);
          }
          ctx.globalAlpha = 1;
        }
      }

      rafRef.current = requestAnimationFrame(tick);
    };

    rafRef.current = requestAnimationFrame(tick);

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      particlesRef.current = [];
    };
  }, [enabled, config.palette, config.gridSize, canvasRef, spawnParticle]);

  return offsets;
}

function randomInterval(): number {
  return 3000 + Math.random() * 2000; // 3-5s
}
